import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import toast from '../../../../utils/toast';
import {
  MoveOutPasses,
  withPassesSentence,
} from '../../../../utils/move-out-passes';
import {
  fetchAllItems,
  updatePropertyOrUserItem,
} from '../../../requests/requests';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import {
  palette,
  spacing,
  radius,
  shadow,
  status,
  typography,
  layout,
} from '../../../../style-utils/theme';
import { down } from '../../../../style-utils/media-queries';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { DashboardEditResident } from './dashboard-edit-resident';
/* WP-37 — THE SHARED ENGINE, replacing this file's private Table/Th/Td.
   These were declared inline here and were, with Units, one of only two correct
   implementations out of six. The engine generalises the reasoning that was
   written at the bottom of this file (clipped header, declared roles) so the
   other four screens get it too — and it SCROLLS rather than clips, which is the
   768px overflow WP-31 measured on this very table. */
import {
  BodyRow,
  Table,
  TableCaption,
  TableFrame,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../../../table/list-table';
import { useResource } from '../../../hooks/use-resource';
import {
  ListSkeleton,
  LoadError,
  skeletonHeight,
} from '../dashboard-shared-components/load-state';
import { Presence, Staleable, StateSwitch } from '../../../../motion';

interface Tenant {
  // The OCCUPANCY id, not the user's. One line per tenancy: since WP-48 the same
  // person may hold two of them in the same organisation, and it is the tenancy
  // that carries the building, the unit and the name on the lease.
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  building_id: number;
  building_name: string | null;
  // DECLARED, AT LAST (WP-31). The list route has returned both of these all
  // along (tenants.js, the GET handler); this interface simply never said so, and
  // a comment asserting "the tenants payload carries no unit" sent this screen
  // off to fetch a whole building roster per building to rediscover them.
  unit_id: number | null;
  unit_number: string | null;
  phone_number: string;
}

// Migration 018 minted a placeholder unit per building, numbered 'UNASSIGNED',
// for every resident who arrived without a unit number. Compared against the
// DB's own normalisation (upper, spaces stripped) so a hand-typed 'unassigned'
// reads the same as the generated one.
const isPlaceholderUnit = (unitNumber: string): boolean =>
  unitNumber.trim().replace(/\s/g, '').toUpperCase() === 'UNASSIGNED';

// A resident on a placeholder is one the front desk cannot look up by door, so
// it reads as an action item (warning tint) rather than as a blank cell.
// `null` as well as `undefined`: the list route returns unit_number as null when
// the unit has no number to give, and the roster lookup this replaced simply left
// the key absent. Both mean the same thing here.
const renderUnitCell = (
  unitNumber: string | null | undefined
): React.ReactNode => {
  if (!unitNumber) {
    return '—';
  }

  if (isPlaceholderUnit(unitNumber)) {
    return <WarningPill>Needs assignment</WarningPill>;
  }

  return unitNumber;
};


const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.sm};
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1 1 260px;
  min-width: 0;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;

  &::placeholder {
    color: ${palette.slateLight};
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const SearchCount = styled.span`
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
`;

/* The list's rows are the table's own height; the skeleton reserves it. */
const SKELETON_ROWS = 5;
// Measured on the rendered table at 1366px.
const ROW_HEIGHT = 76;

interface DashboardTenantsProps {
  // WP-26. Bumped by the parent when the add-resident modal saves. See the note
  // on DashboardUsersProps.refreshToken for why a mount effect is not enough.
  refreshToken?: number;
}

export const DashboardTenants: React.FC<DashboardTenantsProps> = ({
  refreshToken = 0,
}) => {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [tenantToRemove, setTenantToRemove] = useState<Tenant | null>(null);
  /* THE EDIT MODAL STAYS MOUNTED WHILE IT LEAVES. `tenantToEdit` is the last
     resident opened and is never cleared on close; `editOpen` is what the
     modal animates on (FormModal's `open`), so closing plays the exit before
     the portal unmounts. `editSession` keys the instance so reopening — even
     the same resident — starts from a fresh form rather than abandoned edits. */
  const [tenantToEdit, setTenantToEdit] = useState<Tenant | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSession, setEditSession] = useState(0);
  const openEdit = (tenant: Tenant) => {
    setTenantToEdit(tenant);
    setEditSession(session => session + 1);
    setEditOpen(true);
  };
  const closeEdit = () => setEditOpen(false);
  const [search, setSearch] = useState('');
  const canCreateTenant = can(permissions, PERMISSIONS.TENANT_CREATE);
  const canEditTenant = can(permissions, PERMISSIONS.TENANT_EDIT);
  const canDeleteTenant = can(permissions, PERMISSIONS.TENANT_DELETE);
  // The column exists only if there is something in it. A property manager holds
  // tenant:edit and not tenant:delete, so the pair is genuinely independent —
  // gating the header on the delete permission alone (as it was) would have
  // printed an empty "Action" column for that role the moment Edit arrived.
  const showActions = canEditTenant || canDeleteTenant;

  /**
   * `.catch(() => setTenants([]))` WAS THE BUG. A failed load and an org with no
   * residents produced the identical screen — "No residents yet. Add one to give a
   * resident their own dashboard." — so a 403 or a dropped connection invited
   * the admin to re-create residents who already exist.
   */
  const tenantsResource = useResource<Tenant[]>(
    async () => {
      const data = await fetchAllItems('dashboard/tenants');

      return Array.isArray(data) ? (data as Tenant[]) : [];
    },
    { fallbackMessage: 'We could not load your residents.' }
  );

  const tenants = tenantsResource.data || [];
  const loading = tenantsResource.isLoading;
  const load = tenantsResource.reload;

  const seenRefreshToken = useRef(refreshToken);
  useEffect(() => {
    if (seenRefreshToken.current === refreshToken) return;
    seenRefreshToken.current = refreshToken;
    void load();
  }, [refreshToken, load]);

  /* THE UNIT COLUMN USED TO COST ONE REQUEST PER BUILDING (WP-31).
     This screen fetched a whole building roster for every distinct building on
     the page, purely to fill in the Unit cell, on the strength of a comment
     asserting that "the tenants payload carries no unit". It does: the list
     route has returned unit_id and unit_number all along, and the `Tenant`
     interface above simply never declared them. The rosters are gone; the value
     was already in the response this screen had in hand.

     It also removes the reason this component needed `occupancy:read` at all —
     a permission it was checking on a screen already gated on `tenant:read`. */

  const remove = async () => {
    if (!tenantToRemove) {
      return;
    }

    try {
      const result = (await updatePropertyOrUserItem('dashboard/tenants', {
        user_id: tenantToRemove.user_id,
        /* WHICH TENANCY. Since WP-48 the same person can hold two in one
           organisation, and this list renders one line PER TENANCY — so the row
           the reader pressed Delete on already knows which one it is. Sending
           only the user id let the server pick, and it picked whichever row came
           back first: the reader could end the other building's tenancy without
           either of them saying so. `Tenant.id` is the occupancy id; the
           interface above says so. */
        occupancy_id: tenantToRemove.id,
      })) as { passes?: MoveOutPasses | null } | null;
      // Plan 7: what happened to the visitor passes they hosted, if any.
      toast.success(withPassesSentence('Resident deleted', result?.passes));
      setTenantToRemove(null);
      load();
    } catch {
      toast.error('Could not delete resident');
    }
  };

  /**
   * SEARCH. Every sibling people list has one; this table had none, no sort and
   * no pagination, and rendered every resident of every building the caller can
   * see in a single unbounded <tbody>. On a portfolio of any size that is a
   * scroll, not a screen — and the one question this page exists to answer is
   * "which unit is this person in", which needs a name box.
   *
   * Client-side because the payload is already whole: the load fetches all rows
   * regardless, so a server round trip per keystroke would add latency without
   * adding an answer.
   */
  const visibleTenants = useMemo(() => {
    const needle = search.trim().toLowerCase();

    if (!needle) return tenants;

    return tenants.filter(tenant =>
      [
        tenant.first_name,
        tenant.last_name,
        tenant.email,
        tenant.building_name,
        tenant.unit_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [tenants, search]);

  return (
    <Wrap>
      <Header>
        <div>
          <Title>Residents</Title>
          <Sub>Residents with access to their building&apos;s dashboard.</Sub>
        </div>
        {canCreateTenant && (
          <AddButton
            type="button"
            onClick={() => navigate('/dashboard/residents/add-new-resident')}
          >
            + Add new resident
          </AddButton>
        )}
      </Header>

      {!loading && !tenantsResource.error && tenants.length > 0 && (
        <SearchRow>
          <SearchInput
            type="search"
            value={search}
            placeholder="Search by name, email, building or unit"
            aria-label="Search residents"
            onChange={event => setSearch(event.target.value)}
          />
          <SearchCount>
            {visibleTenants.length === tenants.length
              ? `${tenants.length} residents`
              : `${visibleTenants.length} of ${tenants.length} residents`}
          </SearchCount>
        </SearchRow>
      )}

      {/* ONE BOX, FIVE FILLS. StateSwitch crossfades the skeleton, the error,
          the two empty states and the table inside one container with a
          reserved floor. A refetch (after an edit or a delete) is not a state
          change: the table stays and Staleable dims it until the rows land. */}
      <StateSwitch
        state={
          loading
            ? 'loading'
            : tenantsResource.error
              ? 'error'
              : visibleTenants.length === 0 && tenants.length > 0
                ? 'empty-search'
                : tenants.length === 0
                  ? 'empty'
                  : 'ready'
        }
        minHeight={skeletonHeight(SKELETON_ROWS, ROW_HEIGHT)}
      >
        {loading ? (
          <ListSkeleton
            rows={SKELETON_ROWS}
            rowHeight={ROW_HEIGHT}
            label="residents"
          />
        ) : tenantsResource.error ? (
          <LoadError message={tenantsResource.error} onRetry={load} />
        ) : visibleTenants.length === 0 && tenants.length > 0 ? (
          <Empty>
            No resident matches “{search.trim()}”. Clear the search to see all{' '}
            {tenants.length}.
          </Empty>
        ) : tenants.length === 0 ? (
          <Empty>
            No residents yet.
            {canCreateTenant
              ? ' Add one to give a resident their own dashboard.'
              : ''}
          </Empty>
        ) : (
          <Staleable stale={tenantsResource.isRefreshing}>
            <TableFrame>
              <Table>
                <TableCaption>
                  Residents, with the building and unit each one lives in
                </TableCaption>
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Building</Th>
                    <Th>Unit</Th>
                    {showActions && (
                      <Th style={{ textAlign: 'right' }}>Actions</Th>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {/* Rows arrive in a capped cascade and a deleted resident
                      animates out (Presence). The Presence is keyed by the
                      search, so a narrowed list SWAPS and re-cascades rather
                      than watching every non-match fade — a search result is
                      a new list, not a removal. NO `layout` here, deliberately:
                      this list is unpaginated and can run to hundreds of
                      rows, and a layout animation across all of them is what
                      the motion brief forbids for unbounded lists. */}
                  <Presence key={search.trim().toLowerCase()}>
                    {visibleTenants.map((t, index) => {
                      // THE ACCESSIBLE NAME CARRIES THE PERSON (WP-31, and the
                      // same rule WP-36 is scheduled to apply across the
                      // dashboard). A column of buttons all named "Edit" tells
                      // a screen-reader user which control they are on and
                      // nothing about which resident it belongs to; the
                      // visible label stays the one word, because the row it
                      // sits in already says the name.
                      const who = [t.first_name, t.last_name]
                        .filter(Boolean)
                        .join(' ')
                        .trim();

                      return (
                        <BodyRow key={t.id} index={index} layout={false}>
                          <Td data-label="Name">
                            {t.first_name} {t.last_name}
                          </Td>
                          <Td data-label="Email">{t.email}</Td>
                          <Td data-label="Building">{t.building_name || '—'}</Td>
                          <Td data-label="Unit">{renderUnitCell(t.unit_number)}</Td>
                          {showActions && (
                            <Td data-label="Actions" style={{ textAlign: 'right' }}>
                              <RowActions>
                                {canEditTenant && (
                                  <RowBtn
                                    type="button"
                                    aria-label={
                                      who ? `Edit ${who}` : 'Edit this resident'
                                    }
                                    onClick={() => openEdit(t)}
                                  >
                                    Edit
                                  </RowBtn>
                                )}
                                {canDeleteTenant && (
                                  <RemoveBtn
                                    type="button"
                                    aria-label={
                                      who ? `Delete ${who}` : 'Delete this resident'
                                    }
                                    onClick={() => setTenantToRemove(t)}
                                  >
                                    Delete
                                  </RemoveBtn>
                                )}
                              </RowActions>
                            </Td>
                          )}
                        </BodyRow>
                      );
                    })}
                  </Presence>
                </Tbody>
              </Table>
            </TableFrame>
          </Staleable>
        )}
      </StateSwitch>
      {/* ONE DESTRUCTIVE VERB, AND IT SAYS RESIDENT (R-14, R-20 and WP-31's copy
          leak). The row said Remove, the dialog said "Remove tenant", and the
          fallback said "this tenant" — three spellings of one action, two of them
          on a page titled Residents. */}
      <ConfirmDialog
        open={Boolean(tenantToRemove)}
        title="Delete resident"
        message={`Delete ${tenantToRemove?.first_name || 'this resident'} ${
          tenantToRemove?.last_name || ''
        } from the building? This ends their tenancy and revokes their resident access. It cannot be undone.`}
        primaryLabel="Delete"
        primaryVariant="destructive"
        dismissLabel="Cancel"
        onPrimary={remove}
        onDismiss={() => setTenantToRemove(null)}
      />
      {tenantToEdit && (
        <DashboardEditResident
          key={editSession}
          resident={tenantToEdit}
          open={editOpen}
          onSaved={load}
          onClose={closeEdit}
        />
      )}
    </Wrap>
  );
};

const Wrap = styled.div`
  padding: ${spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};

  ${down('sm')} {
    align-items: stretch;
    flex-direction: column;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${typography.size.xl}px;
  font-weight: 700;
  color: ${palette.ink};
`;

const Sub = styled.p`
  margin: 4px 0 0;
  color: ${palette.slate};
`;

const AddButton = styled.button`
  min-height: ${layout.buttonHeight};
  /* A button that never sets a size renders at the browser's own 13.3333px.
     This is the primary action on the Residents screen. */
  font-size: ${typography.size.sm}px;
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.pill};
  background: ${palette.lagoon};
  color: ${palette.white};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;


const Empty = styled.div`
  padding: ${spacing.xl};
  border: 1px dashed ${palette.line};
  border-radius: ${radius.md};
  color: ${palette.slate};
  text-align: center;
`;

/* THE LOCAL TableFrame / Table / Th / Td LIVED HERE — 93 lines of them.
   They are gone to ../../../table/list-table (WP-37), which is the same
   contract this file had reasoned its way to (declared roles, a CLIPPED rather
   than hidden header row) plus the two things it was missing: a scroller
   instead of `overflow: hidden`, and a caption. This screen was one of only two
   correct implementations out of six; the engine is that reasoning, generalised
   so the other four get it too. */
// Status pill per the design system: radius.pill, a status.* {bg, fg, dot}
// triple, Inter 600 12px, leading dot.
const WarningPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${status.warning.bg};
  color: ${status.warning.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;

  &::before {
    content: '';
    width: ${spacing.half};
    height: ${spacing.half};
    border-radius: ${radius.circle};
    background: ${status.warning.dot};
  }
`;

// Two controls in one cell. Right-aligned with the column on a wide screen; on
// the reflowed card layout the Td is already a space-between flex row, so the
// pair sits opposite its "ACTIONS" label and keeps its 44px targets.
const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

const RowBtn = styled.button`
  min-height: ${layout.touchTarget};
  /* Sized, because a button that sets no font-size renders at the browser's own
     13.3333px — which is what Edit and Delete were doing on every row. */
  font-size: ${typography.size.xs}px;
  background: transparent;
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  padding: 0 ${spacing.sm};
  color: ${palette.slate};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

const RemoveBtn = styled.button`
  min-height: ${layout.touchTarget};
  /* Sized for the same reason as RowBtn above — an unsized button renders at the
     browser's 13.3333px. */
  font-size: ${typography.size.xs}px;
  background: transparent;
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  padding: 0 ${spacing.sm};
  color: ${palette.slate};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${status.alert.dot};
    color: ${status.alert.fg};
    background: ${status.alert.bg};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;
